import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { apiKeyExpiryDayOptions, formatApiKeyExpiryDaysLabel, isApiKeyExpired, normalizeApiKeyExpiryDays } from "../apiKeys";
import { lockBodyScroll, unlockBodyScroll } from "./bodyScrollLock";
import { appShellCopy, languageNames, profileMenuCopy, viewLabels } from "./i18n";
import { getIntlLocale, type AppLanguage } from "./language";
import type { ApiKeySummary, AppBranding, ColorMode, Customer, GlobalSearchFilter, GlobalSearchResult, View } from "./types";
import { formatDurationSinceLabel, formatTicketLastUpdateLabel, getTicketSlaCountdownInfo, type DateTimeDisplayMode } from "./utils";
import { getWorkspaceRoleLabel } from "../roles";
import fallbackLogoUrl from "../logo.png";

const profileTimezoneOptions = [
  "UTC-08 · San Francisco",
  "UTC-05 · New York",
  "UTC+00 · London",
  "UTC+01 · Berlin",
  "UTC+08 · Singapore",
  "UTC+09 · Tokyo",
  "UTC+10 · Sydney",
];

function getInitials(name: string) {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase() ?? "")
    .join("") || "KU";
}

function formatApiKeyTimestamp(value: string | null, language: AppLanguage) {
  if (!value) {
    return language === "ja" ? "なし" : "Never";
  }

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleString(getIntlLocale(language), {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function hexToRgb(hex: string) {
  const normalized = hex.replace("#", "");
  const safeHex = normalized.length === 3 ? normalized.split("").map(char => `${char}${char}`).join("") : normalized;
  const value = Number.parseInt(safeHex, 16);

  return {
    r: (value >> 16) & 255,
    g: (value >> 8) & 255,
    b: value & 255,
  };
}

function mixColor(hex: string, amount: number) {
  const { r, g, b } = hexToRgb(hex);
  const target = amount >= 0 ? 255 : 0;
  const intensity = Math.abs(amount);
  const mixChannel = (channel: number) => Math.round(channel + (target - channel) * intensity);

  return `rgb(${mixChannel(r)} ${mixChannel(g)} ${mixChannel(b)})`;
}

function SearchIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="h-4 w-4" xmlns="http://www.w3.org/2000/svg">
      <path d="M8.75 15a6.25 6.25 0 1 0 0-12.5 6.25 6.25 0 0 0 0 12.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="m13.167 13.167 4.166 4.166" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function formatRoleLabel(role: string | null | undefined) {
  return getWorkspaceRoleLabel(role);
}

function getViewLabel(view: View, language: AppLanguage) {
  return viewLabels[language][view];
}

function getTimezoneOptions(timezone: string) {
  return [...new Set([timezone, ...profileTimezoneOptions].filter(Boolean))];
}

function getViewIcon(view: View) {
  const iconClassName = "h-6 w-6";

  switch (view) {
    case "Dashboard":
      return (
        <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className={iconClassName}>
          <path d="M3.75 9.25 10 4.5l6.25 4.75v6A1.25 1.25 0 0 1 15 16.5H5a1.25 1.25 0 0 1-1.25-1.25v-6Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
          <path d="M8 16.5v-4h4v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case "Calendar":
      return (
        <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className={iconClassName}>
          <rect x="3.25" y="4.25" width="13.5" height="12.5" rx="2" stroke="currentColor" strokeWidth="1.5" />
          <path d="M6.25 2.75v3M13.75 2.75v3M3.25 8.25h13.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M6.75 11.25h2.5M10.75 11.25h2.5M6.75 14.25h2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      );
    case "Tickets":
      return (
        <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className={iconClassName}>
          <path d="M5 4.75h10A1.25 1.25 0 0 1 16.25 6v2a1.75 1.75 0 0 0 0 3.5v2A1.25 1.25 0 0 1 15 14.75H5A1.25 1.25 0 0 1 3.75 13.5v-2a1.75 1.75 0 0 0 0-3.5V6A1.25 1.25 0 0 1 5 4.75Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
          <path d="M8 7.5h4M8 10h4M8 12.5h2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      );
    case "Customers":
      return (
        <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className={iconClassName}>
          <path d="M7 5.25v-1a1.25 1.25 0 0 1 1.25-1.25h3.5A1.25 1.25 0 0 1 13 4.25v1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          <rect x="3.25" y="5.25" width="13.5" height="10.5" rx="1.75" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
        </svg>
      );
    case "Items":
      return (
        <svg viewBox="0 -960 960 960" fill="currentColor" aria-hidden="true" className={iconClassName}>
          <path d="M440-183v-274L200-596v274l240 139Zm80 0 240-139v-274L520-457v274Zm-80 92L160-252q-19-11-29.5-29T120-321v-318q0-22 10.5-40t29.5-29l280-161q19-11 40-11t40 11l280 161q19 11 29.5 29t10.5 40v318q0 22-10.5 40T800-252L520-91q-19 11-40 11t-40-11Zm200-528 77-44-237-137-78 45 238 136Zm-160 93 78-45-237-137-78 45 237 137Z" />
        </svg>
      );
    case "Vendors":
      return (
        <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className={iconClassName}>
          <path d="M4 4.75h12l1 3.1a1.9 1.9 0 0 1-3.67.98 1.9 1.9 0 0 1-3.66 0 1.9 1.9 0 0 1-3.34 0 1.9 1.9 0 0 1-3.67-.98L4 4.75Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
          <path d="M5 9.6v5.15A.75.75 0 0 0 5.75 15.5h8.5a.75.75 0 0 0 .75-.75V9.6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M8.25 15.5v-3a.75.75 0 0 1 .75-.75h2a.75.75 0 0 1 .75.75v3" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
        </svg>
      );
    case "Expenses":
      return (
        <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className={iconClassName}>
          <circle cx="10" cy="10" r="7.25" stroke="currentColor" strokeWidth="1.5" />
          <path d="M12 7.75a2.4 2.4 0 0 0-2-1c-1.24 0-2.25.78-2.25 1.75S8.76 10.25 10 10.25s2.25.78 2.25 1.75-1.01 1.75-2.25 1.75a2.4 2.4 0 0 1-2-1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M10 5.5v1M10 13.5v1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      );
    case "Knowledge Base":
      return (
        <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className={`${iconClassName} scale-110`}>
          <path d="M10 5.25C8.75 4.4 7.2 4 5.25 4a.75.75 0 0 0-.75.75v8.5c0 .41.34.75.75.75 1.95 0 3.5.4 4.75 1.25 1.25-.85 2.8-1.25 4.75-1.25a.75.75 0 0 0 .75-.75v-8.5a.75.75 0 0 0-.75-.75C12.8 4 11.25 4.4 10 5.25Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
          <path d="M10 5.25v9.75" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case "Notifications":
      return (
        <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className={iconClassName}>
          <circle cx="10" cy="10" r="7.25" stroke="currentColor" strokeWidth="1.5" />
          <path d="M10 5.75v5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          <path d="M10 14.25h.01" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
        </svg>
      );
    case "Reporting":
      return (
        <svg viewBox="0 -960 960 960" fill="currentColor" aria-hidden="true" className={iconClassName}>
          <path d="M120-240q-33 0-56.5-23.5T40-320q0-33 23.5-56.5T120-400h10.5q4.5 0 9.5 2l182-182q-2-5-2-9.5V-600q0-33 23.5-56.5T400-680q33 0 56.5 23.5T480-600q0 2-2 20l102 102q5-2 9.5-2h21q4.5 0 9.5 2l142-142q-2-5-2-9.5V-640q0-33 23.5-56.5T840-720q33 0 56.5 23.5T920-640q0 33-23.5 56.5T840-560h-10.5q-4.5 0-9.5-2L678-420q2 5 2 9.5v10.5q0 33-23.5 56.5T600-320q-33 0-56.5-23.5T520-400v-10.5q0-4.5 2-9.5L420-522q-5 2-9.5 2H400q-2 0-20-2L198-340q2 5 2 9.5v10.5q0 33-23.5 56.5T120-240Z" />
        </svg>
      );
    case "Logs":
      return (
        <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className={iconClassName}>
          <rect x="3.25" y="3.75" width="13.5" height="12.5" rx="2" stroke="currentColor" strokeWidth="1.5" />
          <path d="M6.5 7h4.75M6.5 10h7M6.5 13h5.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          <circle cx="14.25" cy="7" r="1.25" fill="currentColor" />
        </svg>
      );
    case "Archive":
      return (
        <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className={iconClassName}>
          <path d="M4 5.25h12l-.8 9.13A1.75 1.75 0 0 1 13.46 16H6.54a1.75 1.75 0 0 1-1.74-1.62L4 5.25Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
          <path d="M3 5.25h14V3.5a.75.75 0 0 0-.75-.75H3.75A.75.75 0 0 0 3 3.5v1.75ZM8 8.5h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case "Users":
      return (
        <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className={iconClassName}>
          <circle cx="10" cy="7" r="2.75" stroke="currentColor" strokeWidth="1.5" />
          <path d="M4.75 16.25a5.25 5.25 0 0 1 10.5 0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case "Settings":
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className={iconClassName}>
          <path
            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 0 0 2.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 0 0 1.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 0 0-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 0 0-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 0 0-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 0 0-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 0 0 1.066-2.573c-.94-1.543.826-3.31 2.37-2.37 1 .608 2.296.07 2.572-1.065Z"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path d="M9 12a3 3 0 1 0 6 0a3 3 0 0 0-6 0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    default:
      return (
        <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className={iconClassName}>
          <circle cx="10" cy="10" r="3.5" stroke="currentColor" strokeWidth="1.5" />
        </svg>
      );
  }
}

function getApiKeyExpiryOptions(defaultExpiryDays: number | null) {
  return [
    {
      value: "",
      label: defaultExpiryDays ? `Workspace default (${formatApiKeyExpiryDaysLabel(defaultExpiryDays)})` : "No expiry",
    },
    ...apiKeyExpiryDayOptions.map(days => ({
      value: String(days),
      label: formatApiKeyExpiryDaysLabel(days),
    })),
  ];
}

export function AnimatedModal({
  open,
  onClose,
  overlayClassName,
  dialogClassName,
  backdropAriaLabel,
  dialogLabelledBy,
  dialogDescribedBy,
  overlayStyle,
  dialogStyle,
  children,
}: {
  open: boolean;
  onClose: () => void;
  overlayClassName: string;
  dialogClassName: string;
  backdropAriaLabel: string;
  dialogLabelledBy?: string;
  dialogDescribedBy?: string;
  overlayStyle?: CSSProperties;
  dialogStyle?: CSSProperties;
  children: ReactNode;
}) {
  const [isMounted, setIsMounted] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [renderedChildren, setRenderedChildren] = useState(children);
  const closeAnimationTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    if (open) {
      setRenderedChildren(children);
    }
  }, [children, open]);

  useEffect(() => {
    if (closeAnimationTimeoutRef.current !== null) {
      window.clearTimeout(closeAnimationTimeoutRef.current);
      closeAnimationTimeoutRef.current = null;
    }

    if (open) {
      setIsMounted(true);

      const animationFrameId = window.requestAnimationFrame(() => {
        setIsVisible(true);
      });

      return () => {
        window.cancelAnimationFrame(animationFrameId);
      };
    }

    setIsVisible(false);
    closeAnimationTimeoutRef.current = window.setTimeout(() => {
      setIsMounted(false);
      closeAnimationTimeoutRef.current = null;
    }, 180);

    return () => {
      if (closeAnimationTimeoutRef.current !== null) {
        window.clearTimeout(closeAnimationTimeoutRef.current);
        closeAnimationTimeoutRef.current = null;
      }
    };
  }, [open]);

  useEffect(() => {
    if (!open || typeof document === "undefined") {
      return;
    }

    lockBodyScroll();

    return () => {
      unlockBodyScroll();
    };
  }, [open]);

  useEffect(() => () => {
    if (closeAnimationTimeoutRef.current !== null) {
      window.clearTimeout(closeAnimationTimeoutRef.current);
    }
  }, []);

  if (!isMounted || typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <div className={`${overlayClassName} ${isVisible ? "app-modal-overlay--open" : "app-modal-overlay--closed"}`.trim()} style={overlayStyle}>
      <button
        type="button"
        aria-label={backdropAriaLabel}
        className="absolute inset-0 h-full w-full cursor-default"
        onClick={onClose}
      />

      <div className="pointer-events-none flex min-h-full w-full items-center justify-center">
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby={dialogLabelledBy}
          aria-describedby={dialogDescribedBy}
          className={`pointer-events-auto ${dialogClassName} ${isVisible ? "app-modal-dialog--open" : "app-modal-dialog--closed"}`.trim()}
          style={dialogStyle}
        >
          {open ? children : renderedChildren}
        </div>
      </div>
    </div>,
    document.body,
  );
}

function StatValueSpinner() {
  return (
    <span className="inline-flex h-9 w-9 items-center justify-center text-[var(--brand-primary)]" aria-label="Loading">
      <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="h-7 w-7 animate-spin" xmlns="http://www.w3.org/2000/svg">
        <circle cx="10" cy="10" r="7" stroke="currentColor" strokeOpacity="0.2" strokeWidth="2" />
        <path d="M10 3a7 7 0 0 1 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    </span>
  );
}

export function TicketStatCard({ title, value, detail, accent, isLoading = false }: { title: string; value: string; detail: string; accent: string; isLoading?: boolean }) {
  return (
    <div className="rounded-[24px] bg-white p-6 shadow-[0_20px_40px_rgba(26,28,29,0.02)]">
      <p className="mb-1 text-[10px] font-bold uppercase tracking-[0.28em] text-[#98a2b3]">{title}</p>
      <div className="min-h-[2.25rem]">
        {isLoading ? <StatValueSpinner /> : <p className={`text-3xl font-extrabold tracking-[-0.04em] ${accent}`}>{value}</p>}
      </div>
      <div className="brand-primary-text mt-4 text-xs font-semibold">{detail}</div>
    </div>
  );
}

export function DashboardStatCard({ eyebrow, value, label, accent, isLoading = false }: { eyebrow: string; value: string; label: string; accent: string; isLoading?: boolean }) {
  return (
    <div className="rounded-[24px] bg-white p-6 shadow-[0_20px_60px_rgba(15,23,42,0.06)]">
      <div className="mb-5 flex items-start justify-between gap-4">
        <div className={`flex h-11 w-11 items-center justify-center rounded-2xl text-lg ${accent}`}>{label.slice(0, 1)}</div>
        <span className="text-[10px] font-semibold uppercase tracking-[0.28em] text-[#667085]">{eyebrow}</span>
      </div>
      <div className="min-h-[2.25rem]">
        {isLoading ? <StatValueSpinner /> : <p className="text-3xl font-extrabold tracking-[-0.04em] text-[#101828]">{value}</p>}
      </div>
      <p className="mt-1 text-xs font-medium uppercase tracking-[0.2em] text-[#98a2b3]">{label}</p>
    </div>
  );
}

export function Avatar({ customer }: { customer: Customer }) {
  return (
    <div className="brand-primary-avatar flex h-8 w-8 items-center justify-center rounded-full text-[10px] font-bold">
      {customer.initials}
    </div>
  );
}

export function UserAvatarBadge({
  avatarUrl,
  initials,
  className,
}: {
  avatarUrl: string | null;
  initials: string;
  className: string;
}) {
  const [imageFailed, setImageFailed] = useState(false);

  useEffect(() => {
    setImageFailed(false);
  }, [avatarUrl]);

  return (
    <div className={className}>
      {avatarUrl && !imageFailed ? (
        <img
          src={avatarUrl}
          alt="User avatar"
          className="h-full w-full rounded-full object-cover"
          onError={() => setImageFailed(true)}
        />
      ) : (
        initials
      )}
    </div>
  );
}

export function TicketLastUpdateText({
  value,
  timezone,
  className,
  fallback = "Unknown",
  as = "span",
}: {
  value: string | null | undefined;
  timezone: string;
  className?: string;
  fallback?: string;
  as?: "button" | "span";
}) {
  const [displayMode, setDisplayMode] = useState<DateTimeDisplayMode>("relative");
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    setDisplayMode("relative");
  }, [value]);

  useEffect(() => {
    if (displayMode !== "relative") {
      return;
    }

    const intervalId = window.setInterval(() => {
      setNow(Date.now());
    }, 60_000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [displayMode]);

  const label = formatTicketLastUpdateLabel(value, timezone, displayMode, fallback, now);
  const alternateLabel = formatTicketLastUpdateLabel(value, timezone, displayMode === "relative" ? "absolute" : "relative", fallback, now);
  const sharedClassName = `${className ?? ""} inline-flex items-center rounded-md text-inherit transition-colors hover:text-[#101828] focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary-ring)]`.trim();

  const handleToggle = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
    event.stopPropagation();
    setDisplayMode(current => (current === "relative" ? "absolute" : "relative"));
  };

  if (as === "button") {
    return (
      <button
        type="button"
        onClick={handleToggle}
        className={sharedClassName}
        aria-label={`Toggle ticket last update format. Currently showing ${label}.`}
        title={alternateLabel}
      >
        {label}
      </button>
    );
  }

  return (
    <span
      role="button"
      tabIndex={0}
      onClick={handleToggle}
      onKeyDown={event => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          handleToggle(event);
        }
      }}
      className={sharedClassName}
      aria-label={`Toggle ticket last update format. Currently showing ${label}.`}
      title={alternateLabel}
    >
      {label}
    </span>
  );
}

export function TicketDurationText({
  value,
  fallback = "Unknown",
}: {
  value: string | null | undefined;
  fallback?: string;
}) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    setNow(Date.now());

    const intervalId = window.setInterval(() => {
      setNow(Date.now());
    }, 60_000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [value]);

  return <>{formatDurationSinceLabel(value, fallback, now)}</>;
}

export function TicketSlaCountdownText({
  createdAt,
  sla,
  slaPausedAt,
  slaPausedTotalMs,
  fallback = "--",
}: {
  createdAt: string | null | undefined;
  sla: string | null | undefined;
  slaPausedAt?: string | null | undefined;
  slaPausedTotalMs?: number | null | undefined;
  fallback?: string;
}) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    setNow(Date.now());

    const intervalId = window.setInterval(() => {
      setNow(Date.now());
    }, 60_000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [createdAt, sla, slaPausedAt, slaPausedTotalMs]);

  const countdown = getTicketSlaCountdownInfo(createdAt, sla, now, slaPausedAt, slaPausedTotalMs);

  if (!countdown) {
    return <>{fallback}</>;
  }

  return <>{countdown.isPaused ? `Paused · ${countdown.displayLabel}` : countdown.displayLabel}</>;
}

export function DeleteAccountButton({
  onDeleteAccount,
  className,
}: {
  onDeleteAccount: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      aria-label="Delete customer"
      title="Delete customer"
      onClick={event => {
        event.stopPropagation();
        onDeleteAccount();
      }}
      className={`${className ?? ""} flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-white/60 bg-white text-[#b42318] shadow-[0_20px_40px_rgba(26,28,29,0.04)] transition-all hover:border-[#fecaca] hover:bg-[#fef2f2] hover:text-[#912018] focus:outline-none focus:ring-2 focus:ring-[#b42318]/15 disabled:cursor-not-allowed disabled:text-[#d0d5dd] disabled:hover:border-white/60 disabled:hover:bg-white`.trim()}
    >
      <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="h-4 w-4" xmlns="http://www.w3.org/2000/svg">
        <path d="M5.833 7.5v6.25A1.667 1.667 0 0 0 7.5 15.417h5a1.667 1.667 0 0 0 1.667-1.667V7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M4.167 5.417h11.666" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        <path d="M7.917 5.417V4.583c0-.46.373-.833.833-.833h2.5c.46 0 .833.373.833.833v.834" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M8.75 9.583v3.334" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        <path d="M11.25 9.583v3.334" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
      <span className="sr-only">Delete customer</span>
    </button>
  );
}

function ProfileMenu({
  language,
  currentUser,
  currentUserEmail,
  currentUserAvatarUrl,
  currentUserRole,
  apiAccessEnabled,
  defaultApiKeyExpiryDays,
  navMenuMode,
  canManageApiKeys,
  canGenerateApiKeys,
  apiKeys,
  latestGeneratedApiKey,
  userInitials,
  themeMode,
  timezone,
  onOpenSettings,
  onGenerateApiKey,
  onDismissGeneratedApiKey,
  onRevokeApiKey,
  onAvatarUpload,
  onAvatarRemove,
  onUpdatePassword,
  onThemeModeChange,
  onLanguageChange,
  onNavMenuModeChange,
  onTimezoneChange,
  onSignOut,
}: {
  language: AppLanguage;
  currentUser: string;
  currentUserEmail: string | null;
  currentUserAvatarUrl: string | null;
  currentUserRole: string | null;
  apiAccessEnabled: boolean;
  defaultApiKeyExpiryDays: number | null;
  navMenuMode: "labels" | "icons";
  canManageApiKeys: boolean;
  canGenerateApiKeys: boolean;
  apiKeys: ApiKeySummary[];
  latestGeneratedApiKey: string | null;
  userInitials: string;
  themeMode: ColorMode;
  timezone: string;
  onOpenSettings?: () => void;
  onGenerateApiKey: (label: string, expiryDays: number | null) => Promise<void>;
  onDismissGeneratedApiKey: () => void;
  onRevokeApiKey: (apiKeyId: string) => Promise<void>;
  onAvatarUpload?: (file: File) => Promise<void>;
  onAvatarRemove?: () => Promise<void>;
  onUpdatePassword?: (currentPassword: string, newPassword: string) => Promise<void>;
  onThemeModeChange: (value: ColorMode) => void;
  onLanguageChange: (value: AppLanguage) => void;
  onNavMenuModeChange: (value: "labels" | "icons") => void;
  onTimezoneChange: (value: string) => void;
  onSignOut?: () => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [apiKeyLabel, setApiKeyLabel] = useState("");
  const [apiKeyExpiryValue, setApiKeyExpiryValue] = useState("");
  const [apiKeyError, setApiKeyError] = useState("");
  const [avatarError, setAvatarError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [passwordNotice, setPasswordNotice] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isGeneratingApiKey, setIsGeneratingApiKey] = useState(false);
  const [isUpdatingAvatar, setIsUpdatingAvatar] = useState(false);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [revokingApiKeyId, setRevokingApiKeyId] = useState<string | null>(null);
  const [copyState, setCopyState] = useState<"idle" | "copied" | "error">("idle");
  const isDarkMode = themeMode === "dark";
  const copy = profileMenuCopy[language];
  const roleLabel = formatRoleLabel(currentUserRole);
  const timezones = getTimezoneOptions(timezone);
  const apiKeyExpiryOptions = getApiKeyExpiryOptions(defaultApiKeyExpiryDays);
  const canManageAvatar = Boolean(onAvatarUpload);
  const dialogTitleId = "profile-settings-dialog-title";
  const dialogDescriptionId = "profile-settings-dialog-description";
  const profileDialogStyle = isDarkMode
    ? {
        borderColor: "rgba(255, 255, 255, 0.1)",
        backgroundColor: "#1f1f24",
        boxShadow: "0 36px 120px rgba(0, 0, 0, 0.46)",
      }
    : undefined;
  const profileCardStyle = isDarkMode
    ? {
        borderColor: "rgba(255, 255, 255, 0.1)",
        boxShadow: "none",
      }
    : undefined;
  const profileCardClassName = `rounded-[22px] border px-5 py-4 ${isDarkMode ? "" : "border-white/70 bg-white shadow-[0_18px_40px_rgba(15,23,42,0.05)]"}`.trim();
  const profileFieldStyle = isDarkMode
    ? {
        borderColor: "rgba(255, 255, 255, 0.1)",
        backgroundColor: "#232327",
        color: "#fafafa",
      }
    : undefined;
  const profileSubtleButtonStyle = isDarkMode
    ? {
        borderColor: "rgba(255, 255, 255, 0.1)",
        backgroundColor: "#232327",
        color: "#e4e4e7",
      }
    : undefined;
  const profileDangerButtonStyle = isDarkMode
    ? {
        borderColor: "rgba(248, 113, 113, 0.22)",
        backgroundColor: "rgba(127, 29, 29, 0.16)",
        color: "#fecaca",
      }
    : undefined;
  const generatedApiKeyCardStyle = isDarkMode
    ? {
        borderColor: "rgba(var(--brand-primary-rgb), 0.28)",
        background: "linear-gradient(135deg, rgba(var(--brand-primary-rgb), 0.28), rgba(var(--brand-primary-rgb), 0.18))",
        boxShadow: "inset 0 1px 0 rgba(255, 255, 255, 0.04)",
      }
    : undefined;
  const generatedApiKeyCopyButtonStyle = isDarkMode
    ? {
        borderColor: "rgba(var(--brand-primary-rgb), 0.32)",
        backgroundColor: "rgba(var(--brand-primary-rgb), 0.14)",
        color: "#f8fafc",
      }
    : undefined;
  const generatedApiKeyDismissButtonStyle = isDarkMode
    ? {
        borderColor: "rgba(255, 255, 255, 0.14)",
        backgroundColor: "rgba(24, 24, 27, 0.34)",
        color: "#f4f4f5",
      }
    : undefined;

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    onOpenSettings?.();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onOpenSettings]);

  useEffect(() => {
    if (!latestGeneratedApiKey || copyState !== "copied") {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setCopyState("idle");
    }, 1800);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [copyState, latestGeneratedApiKey]);

  const handleGenerateApiKey = async () => {
    setApiKeyError("");
    setIsGeneratingApiKey(true);

    try {
      await onGenerateApiKey(apiKeyLabel, normalizeApiKeyExpiryDays(apiKeyExpiryValue, null));
      setApiKeyLabel("");
      setApiKeyExpiryValue("");
      setCopyState("idle");
    } catch (error) {
      setApiKeyError(error instanceof Error ? error.message : copy.createApiKeyError);
    } finally {
      setIsGeneratingApiKey(false);
    }
  };

  const handleCopyApiKey = async () => {
    if (!latestGeneratedApiKey) {
      return;
    }

    try {
      await navigator.clipboard.writeText(latestGeneratedApiKey);
      setCopyState("copied");
    } catch {
      setCopyState("error");
    }
  };

  const handleRevokeApiKey = async (apiKeyId: string, isRevoked: boolean) => {
    setApiKeyError("");
    setRevokingApiKeyId(apiKeyId);

    try {
      await onRevokeApiKey(apiKeyId);
    } catch (error) {
      setApiKeyError(error instanceof Error ? error.message : isRevoked ? copy.deleteApiKeyError : copy.revokeApiKeyError);
    } finally {
      setRevokingApiKeyId(null);
    }
  };

  const handleAvatarSelection = async (file: File | null) => {
    if (!file || !onAvatarUpload) {
      return;
    }

    setAvatarError("");
    setIsUpdatingAvatar(true);

    try {
      await onAvatarUpload(file);
    } catch (error) {
      setAvatarError(error instanceof Error ? error.message : copy.updateAvatarError);
    } finally {
      setIsUpdatingAvatar(false);
    }
  };

  const handleAvatarRemoval = async () => {
    if (!onAvatarRemove) {
      return;
    }

    setAvatarError("");
    setIsUpdatingAvatar(true);

    try {
      await onAvatarRemove();
    } catch (error) {
      setAvatarError(error instanceof Error ? error.message : copy.removeAvatarError);
    } finally {
      setIsUpdatingAvatar(false);
    }
  };

  const handlePasswordUpdate = async () => {
    if (!onUpdatePassword) {
      return;
    }

    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError(copy.passwordRequired);
      setPasswordNotice("");
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError(copy.passwordTooShort);
      setPasswordNotice("");
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError(copy.passwordConfirmationMismatch);
      setPasswordNotice("");
      return;
    }

    setPasswordError("");
    setPasswordNotice("");
    setIsUpdatingPassword(true);

    try {
      await onUpdatePassword(currentPassword, newPassword);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setPasswordNotice(copy.passwordUpdated);
    } catch (error) {
      setPasswordError(error instanceof Error ? error.message : copy.updatePasswordError);
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  return (
    <div className="relative" onClick={event => event.stopPropagation()}>
      <button
        type="button"
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-label={copy.openProfileSettings}
        onClick={() => setIsOpen(current => !current)}
        className="brand-primary-focus-ring flex items-center gap-1.5 rounded-full border px-1.5 py-1 text-left transition-colors"
        style={{
          borderColor: isDarkMode ? "rgba(115, 115, 115, 0.42)" : "rgba(208, 213, 221, 0.9)",
          backgroundColor: isDarkMode ? "rgba(38, 38, 38, 0.92)" : "rgba(255, 255, 255, 0.88)",
          color: isDarkMode ? "#f8fafc" : "#1a1c1d",
          boxShadow: isDarkMode ? "0 16px 36px rgba(0, 0, 0, 0.34)" : "0 12px 28px rgba(15, 23, 42, 0.08)",
        }}
      >
        <UserAvatarBadge
          avatarUrl={currentUserAvatarUrl}
          initials={userInitials}
          className="brand-primary-avatar flex h-8 w-8 items-center justify-center rounded-full text-[11px] font-bold ring-2 ring-white/60 shadow-sm"
        />
        <span style={{ color: isDarkMode ? "#a3a3a3" : "#98a2b3" }} className="pr-0.5 text-xs">
          ▾
        </span>
      </button>

      <AnimatedModal
        open={isOpen}
        onClose={() => setIsOpen(false)}
        backdropAriaLabel={copy.closeProfileSettings}
        dialogLabelledBy={dialogTitleId}
        dialogDescribedBy={dialogDescriptionId}
        overlayClassName="fixed inset-0 z-[95] flex items-center justify-center bg-[#09090b]/72 p-4 backdrop-blur-[6px] sm:p-6"
        dialogClassName="app-modal-dialog relative z-10 flex max-h-[calc(100vh-4rem)] w-full max-w-4xl flex-col overflow-hidden rounded-[32px] border border-white/70 bg-[#fcfcfd] p-6 shadow-[0_32px_100px_rgba(15,23,42,0.24)]"
        dialogStyle={profileDialogStyle}
      >
        <div className="flex min-h-0 flex-1 flex-col">
        <div className="relative shrink-0">
          <div className="min-w-0">
              <div className="pr-16">
              <p className="text-[10px] font-semibold uppercase tracking-[0.24em]" style={{ color: isDarkMode ? "#a1a1aa" : "#98a2b3" }}>{copy.profileSettings}</p>
              <h2 id={dialogTitleId} className="mt-2 font-headline text-3xl font-extrabold tracking-[-0.05em]" style={{ color: isDarkMode ? "#fafafa" : "#101828" }}>
                {copy.preferences}
              </h2>
              <p id={dialogDescriptionId} className="mt-2 max-w-2xl text-sm" style={{ color: isDarkMode ? "#a1a1aa" : "#667085" }}>
                {copy.preferencesDescription}
              </p>
              </div>
          </div>

          <button
            type="button"
            onClick={() => setIsOpen(false)}
            className="absolute top-0 right-0 flex h-11 w-11 items-center justify-center rounded-2xl border-0 bg-white text-lg font-semibold text-slate-500 shadow-[0_12px_24px_rgba(15,23,42,0.08)] transition-colors hover:bg-[#f3f4f6] hover:text-[#1a1c1d]"
            style={isDarkMode ? { backgroundColor: "#2b2b30", color: "#fafafa", boxShadow: "none" } : undefined}
            aria-label={copy.closeProfileSettings}
          >
            ×
          </button>
        </div>

        <div className="custom-scrollbar mt-4 min-h-0 flex-1 overflow-y-auto pr-1">
              <div className="grid gap-4 lg:grid-cols-2">
              <section className={profileCardClassName} style={profileCardStyle}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0 flex-1">
                          <p className="text-[10px] font-bold uppercase tracking-[0.24em]" style={{ color: isDarkMode ? "#a1a1aa" : "#94a3b8" }}>{copy.profilePhoto}</p>
                          <p className="mt-2 text-sm font-semibold" style={{ color: isDarkMode ? "#fafafa" : "#101828" }}>{copy.profilePhotoTitle}</p>
                          <p className="mt-1 text-sm" style={{ color: isDarkMode ? "#d4d4d8" : "#667085" }}>{copy.profilePhotoDescription}</p>
                        </div>
                        {canManageAvatar ? (
                          <label
                            className={`block shrink-0 ${isUpdatingAvatar ? "cursor-progress opacity-70" : "cursor-pointer"}`}
                            aria-label={currentUserAvatarUrl ? copy.replacePhoto : copy.uploadPhoto}
                            title={currentUserAvatarUrl ? copy.replacePhoto : copy.uploadPhoto}
                          >
                            <input
                              type="file"
                              accept="image/png,image/jpeg,image/webp"
                              className="hidden"
                              disabled={isUpdatingAvatar}
                              onChange={event => {
                                const file = event.target.files?.[0] ?? null;
                                event.target.value = "";
                                void handleAvatarSelection(file);
                              }}
                            />
                            <UserAvatarBadge
                              avatarUrl={currentUserAvatarUrl}
                              initials={userInitials}
                              className="brand-primary-avatar flex h-16 w-16 items-center justify-center rounded-full text-lg font-bold shadow-[0_16px_40px_rgba(15,23,42,0.12)] transition-transform hover:scale-[1.03]"
                            />
                          </label>
                        ) : (
                          <UserAvatarBadge
                            avatarUrl={currentUserAvatarUrl}
                            initials={userInitials}
                            className="brand-primary-avatar flex h-16 w-16 shrink-0 items-center justify-center rounded-full text-lg font-bold shadow-[0_16px_40px_rgba(15,23,42,0.12)]"
                          />
                        )}
                      </div>

                      <div className="flex flex-wrap items-center gap-3">
                        {!canManageAvatar ? (
                          <span className="rounded-xl border px-4 py-2.5 text-sm font-semibold text-[#98a2b3]" style={profileSubtleButtonStyle}>
                            {copy.profilePhotoUnavailable}
                          </span>
                        ) : null}

                        {currentUserAvatarUrl && onAvatarRemove ? (
                          <button
                            type="button"
                            onClick={() => void handleAvatarRemoval()}
                            disabled={isUpdatingAvatar}
                            className="rounded-xl border bg-[#fef3f2] px-4 py-2.5 text-sm font-semibold text-[#b42318] transition-colors hover:bg-[#fee4e2] disabled:cursor-not-allowed disabled:opacity-60"
                            style={profileDangerButtonStyle}
                          >
                            {copy.removePhoto}
                          </button>
                        ) : null}
                      </div>

                      {avatarError ? <p className="text-sm font-semibold text-[#b42318]">{avatarError}</p> : null}
                  </section>

                  <section className={profileCardClassName} style={profileCardStyle}>
                    <p className="text-[10px] font-bold uppercase tracking-[0.24em]" style={{ color: isDarkMode ? "#a1a1aa" : "#94a3b8" }}>{copy.session}</p>
                    <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold" style={{ color: isDarkMode ? "#fafafa" : "#101828" }}>{currentUser} : {roleLabel}</p>
                        {currentUserEmail ? (
                          <p className="mt-1 text-sm" style={{ color: isDarkMode ? "#d4d4d8" : "#667085" }}>{currentUserEmail}</p>
                        ) : null}
                      </div>

                      {onSignOut ? (
                        <button
                          type="button"
                          onClick={() => {
                            setIsOpen(false);
                            onSignOut();
                          }}
                          className="shrink-0 rounded-xl border bg-[#fef3f2] px-4 py-2.5 text-sm font-semibold text-[#b42318] transition-colors hover:bg-[#fee4e2]"
                          style={profileDangerButtonStyle}
                        >
                          {copy.logOut}
                        </button>
                      ) : null}
                    </div>
                  </section>
              </div>

        <div className="mt-6 grid gap-4 lg:grid-cols-2">

          <section className={profileCardClassName} style={profileCardStyle}>
            <p className="text-[10px] font-bold uppercase tracking-[0.24em]" style={{ color: isDarkMode ? "#a1a1aa" : "#94a3b8" }}>{copy.appearance}</p>
            <p className="mt-2 text-sm" style={{ color: isDarkMode ? "#d4d4d8" : "#667085" }}>{copy.appearanceDescription}</p>
            <div className="mt-4 grid grid-cols-2 gap-2">
              {(["light", "dark"] as ColorMode[]).map(mode => {
                const active = themeMode === mode;
                const modeCopy = copy[mode];

                return (
                  <button
                    key={mode}
                    type="button"
                    aria-pressed={active}
                    onClick={() => onThemeModeChange(mode)}
                    className="rounded-[18px] px-4 py-3 text-left text-sm font-semibold transition-colors"
                    style={{
                      backgroundColor: active ? "var(--brand-primary-soft)" : isDarkMode ? "#242424" : "#ffffff",
                      color: active ? "var(--brand-primary)" : isDarkMode ? "#fafafa" : "#344054",
                      border: `1px solid ${active ? "rgba(var(--brand-primary-rgb), 0.36)" : isDarkMode ? "rgba(255, 255, 255, 0.14)" : "#eaecf0"}`,
                      boxShadow: isDarkMode ? "inset 0 1px 0 rgba(255, 255, 255, 0.04)" : undefined,
                    }}
                  >
                    <span className="block">{modeCopy.title}</span>
                    <span className="mt-1 block text-xs font-medium" style={{ color: active ? "currentColor" : isDarkMode ? "#a1a1aa" : "#98a2b3" }}>
                      {modeCopy.detail}
                    </span>
                  </button>
                );
              })}
              </div>
          </section>

          <section className={profileCardClassName} style={profileCardStyle}>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.24em]" style={{ color: isDarkMode ? "#a1a1aa" : "#94a3b8" }}>{copy.navigation}</p>
              <p className="mt-2 text-sm" style={{ color: isDarkMode ? "#d4d4d8" : "#667085" }}>{copy.navigationDescription}</p>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2">
              {([
                { value: "labels", title: copy.labels.title, detail: copy.labels.detail },
                { value: "icons", title: copy.icons.title, detail: copy.icons.detail },
              ] as const).map(option => {
                const active = navMenuMode === option.value;

                return (
                  <button
                    key={option.value}
                    type="button"
                    aria-pressed={active}
                    onClick={() => onNavMenuModeChange(option.value)}
                    className="rounded-[18px] px-4 py-3 text-left text-sm font-semibold transition-colors"
                    style={{
                      backgroundColor: active ? "var(--brand-primary-soft)" : isDarkMode ? "#242424" : "#ffffff",
                      color: active ? "var(--brand-primary)" : isDarkMode ? "#fafafa" : "#344054",
                      border: `1px solid ${active ? "rgba(var(--brand-primary-rgb), 0.36)" : isDarkMode ? "rgba(255, 255, 255, 0.14)" : "#eaecf0"}`,
                      boxShadow: isDarkMode ? "inset 0 1px 0 rgba(255, 255, 255, 0.04)" : undefined,
                    }}
                  >
                    <span className="block">{option.title}</span>
                    <span className="mt-1 block text-xs font-medium" style={{ color: active ? "currentColor" : isDarkMode ? "#a1a1aa" : "#98a2b3" }}>
                      {option.detail}
                    </span>
                  </button>
                );
              })}
            </div>
          </section>

          <section className={profileCardClassName} style={profileCardStyle}>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.24em]" style={{ color: isDarkMode ? "#a1a1aa" : "#94a3b8" }}>{copy.timezone}</p>
              <p className="mt-2 text-sm" style={{ color: isDarkMode ? "#d4d4d8" : "#667085" }}>{copy.timezoneDescription}</p>
            </div>
            <div className="mt-4">
              <select
                value={timezone}
                onChange={event => onTimezoneChange(event.target.value)}
                className="brand-primary-focus-ring mt-4 w-full rounded-[18px] border border-[#d0d5dd] bg-white px-4 py-3 text-sm font-semibold text-[#101828] outline-none"
                style={{
                  marginTop: 0,
                  ...profileFieldStyle,
                }}
              >
                {timezones.map(option => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
          </section>

          <section className={profileCardClassName} style={profileCardStyle}>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.24em]" style={{ color: isDarkMode ? "#a1a1aa" : "#94a3b8" }}>{copy.language}</p>
              <p className="mt-2 text-sm" style={{ color: isDarkMode ? "#d4d4d8" : "#667085" }}>{copy.languageDescription}</p>
            </div>
            <div className="mt-4">
              <select
                value={language}
                onChange={event => onLanguageChange(event.target.value as AppLanguage)}
                className="brand-primary-focus-ring w-full rounded-[18px] border border-[#d0d5dd] bg-white px-4 py-3 text-sm font-semibold text-[#101828] outline-none"
                style={profileFieldStyle}
                aria-label={copy.language}
              >
                <option value="en">{languageNames[language].en}</option>
                <option value="ja">{languageNames[language].ja}</option>
              </select>
            </div>
          </section>

          {onUpdatePassword ? (
            <section className={`${profileCardClassName} lg:col-span-2`} style={profileCardStyle}>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.24em]" style={{ color: isDarkMode ? "#a1a1aa" : "#94a3b8" }}>{copy.security}</p>
                <p className="mt-2 text-sm font-semibold" style={{ color: isDarkMode ? "#fafafa" : "#101828" }}>{copy.securityTitle}</p>
                <p className="mt-1 text-sm" style={{ color: isDarkMode ? "#d4d4d8" : "#667085" }}>
                  {copy.securityDescription}
                </p>
              </div>

              <div className="mt-4 grid gap-3 md:grid-cols-3">
                <input
                  type="password"
                  value={currentPassword}
                  onChange={event => setCurrentPassword(event.target.value)}
                  placeholder={copy.currentPassword}
                  autoComplete="current-password"
                  disabled={isUpdatingPassword}
                  className="brand-primary-focus-ring w-full rounded-[18px] border border-[#d0d5dd] bg-white px-4 py-3 text-sm font-medium text-[#101828] outline-none disabled:cursor-not-allowed disabled:opacity-60"
                  style={profileFieldStyle}
                />
                <input
                  type="password"
                  value={newPassword}
                  onChange={event => setNewPassword(event.target.value)}
                  placeholder={copy.newPassword}
                  autoComplete="new-password"
                  disabled={isUpdatingPassword}
                  className="brand-primary-focus-ring w-full rounded-[18px] border border-[#d0d5dd] bg-white px-4 py-3 text-sm font-medium text-[#101828] outline-none disabled:cursor-not-allowed disabled:opacity-60"
                  style={profileFieldStyle}
                />
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={event => setConfirmPassword(event.target.value)}
                  placeholder={copy.confirmNewPassword}
                  autoComplete="new-password"
                  disabled={isUpdatingPassword}
                  className="brand-primary-focus-ring w-full rounded-[18px] border border-[#d0d5dd] bg-white px-4 py-3 text-sm font-medium text-[#101828] outline-none disabled:cursor-not-allowed disabled:opacity-60"
                  style={profileFieldStyle}
                />
              </div>

              <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                <p className="text-xs font-medium" style={{ color: isDarkMode ? "#a1a1aa" : "#667085" }}>
                  {copy.passwordHint}
                </p>
                <button
                  type="button"
                  onClick={() => void handlePasswordUpdate()}
                  disabled={isUpdatingPassword}
                  className={`rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors ${isUpdatingPassword ? "cursor-not-allowed bg-[#e4e7ec] text-[#98a2b3]" : "brand-primary-button"}`}
                >
                  {isUpdatingPassword ? copy.updatingPassword : copy.updatePassword}
                </button>
              </div>

              {passwordNotice ? <p className="mt-3 text-sm font-semibold text-[#027a48]">{passwordNotice}</p> : null}
              {passwordError ? <p className="mt-3 text-sm font-semibold text-[#b42318]">{passwordError}</p> : null}
            </section>
          ) : null}

          {apiAccessEnabled && canManageApiKeys ? (
            <section className={`${profileCardClassName} lg:col-span-2`} style={profileCardStyle}>
              <div className="flex flex-col gap-4">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.24em]" style={{ color: isDarkMode ? "#a1a1aa" : "#94a3b8" }}>{copy.apiAccess}</p>
                  <p className="mt-2 text-sm font-semibold" style={{ color: isDarkMode ? "#fafafa" : "#101828" }}>
                    {copy.apiAccessTitle}
                  </p>
                  <p className="mt-1 text-sm" style={{ color: isDarkMode ? "#d4d4d8" : "#667085" }}>
                    {copy.apiAccessDescription}
                  </p>
                  {defaultApiKeyExpiryDays ? (
                    <p className="mt-2 text-xs font-medium" style={{ color: isDarkMode ? "#d4d4d8" : "#475467" }}>
                      {copy.tenantPolicyExpires(formatApiKeyExpiryDaysLabel(defaultApiKeyExpiryDays))}
                    </p>
                  ) : null}
                </div>

                <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <input
                    type="text"
                    value={apiKeyLabel}
                    onChange={event => setApiKeyLabel(event.target.value)}
                    placeholder={copy.optionalLabel}
                    disabled={isGeneratingApiKey}
                    className="brand-primary-focus-ring w-full rounded-[18px] border border-[#d0d5dd] bg-white px-4 py-3 text-sm font-medium text-[#101828] outline-none disabled:cursor-not-allowed disabled:opacity-60 sm:max-w-[360px] sm:flex-1"
                    style={profileFieldStyle}
                  />
                  <select
                    value={apiKeyExpiryValue}
                    onChange={event => setApiKeyExpiryValue(event.target.value)}
                    disabled={isGeneratingApiKey}
                    className="brand-primary-focus-ring w-full rounded-[18px] border border-[#d0d5dd] bg-white px-4 py-3 text-sm font-medium text-[#101828] outline-none disabled:cursor-not-allowed disabled:opacity-60 sm:max-w-[220px]"
                    style={profileFieldStyle}
                    aria-label={copy.apiKeyExpiry}
                  >
                    {apiKeyExpiryOptions.map(option => (
                      <option key={option.value || "default"} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => void handleGenerateApiKey()}
                    disabled={!canGenerateApiKeys || isGeneratingApiKey}
                    className={`rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors sm:shrink-0 ${
                      canGenerateApiKeys && !isGeneratingApiKey
                        ? "brand-primary-button"
                        : "cursor-not-allowed bg-[#e4e7ec] text-[#98a2b3]"
                    }`}
                  >
                    {isGeneratingApiKey ? copy.generatingApiKey : copy.generateApiKey}
                  </button>
                </div>
              </div>

              {latestGeneratedApiKey ? (
                <div className="mt-5 rounded-[20px] border border-[var(--brand-primary-ring)] bg-[var(--brand-primary-soft)] px-4 py-4" style={generatedApiKeyCardStyle}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="brand-primary-text text-[10px] font-semibold uppercase tracking-[0.22em]" style={{ color: isDarkMode ? "#dcfce7" : undefined }}>{copy.newKey}</p>
                      <p className="mt-2 break-all font-mono text-sm text-[var(--brand-primary-strong)]" style={{ color: isDarkMode ? "#f0fdf4" : undefined }}>{latestGeneratedApiKey}</p>
                      <p className="mt-2 text-xs" style={{ color: isDarkMode ? "rgba(240, 253, 244, 0.84)" : "var(--brand-primary-strong)" }}>{copy.copyKeyNow}</p>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      <button type="button" onClick={() => void handleCopyApiKey()} className="rounded-xl bg-white px-3 py-2 text-sm font-semibold text-[var(--brand-primary)] ring-1 ring-[var(--brand-primary-ring)] transition-colors hover:bg-[var(--brand-primary-soft)]" style={generatedApiKeyCopyButtonStyle}>
                        {copyState === "copied" ? copy.copied : copy.copy}
                      </button>
                      <button type="button" onClick={onDismissGeneratedApiKey} className="rounded-xl bg-white px-3 py-2 text-sm font-semibold text-[#475467] ring-1 ring-[#d0d5dd] transition-colors hover:bg-[#f9fafb]" style={generatedApiKeyDismissButtonStyle}>
                        {copy.dismiss}
                      </button>
                    </div>
                  </div>
                  {copyState === "error" ? <p className="mt-2 text-xs font-semibold text-[#b42318]" style={{ color: isDarkMode ? "#fee2e2" : undefined }}>{copy.copyKeyError}</p> : null}
                </div>
              ) : null}

              <div className="custom-scrollbar mt-5 max-h-[320px] space-y-3 overflow-y-auto pr-1">
                {apiKeys.length > 0 ? apiKeys.map(apiKey => (
                  <div
                    key={apiKey.id}
                    className="flex flex-col gap-3 rounded-[18px] border border-[#eaecf0] bg-[#fcfcfd] px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
                    style={isDarkMode ? {
                      borderColor: apiKey.revokedAt ? "rgba(244, 114, 182, 0.18)" : "rgba(255, 255, 255, 0.14)",
                      backgroundColor: apiKey.revokedAt ? "rgba(64, 64, 64, 0.8)" : "#242424",
                      boxShadow: "inset 0 1px 0 rgba(255, 255, 255, 0.04)",
                    } : undefined}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold" style={{ color: isDarkMode ? "#fafafa" : "#101828" }}>{apiKey.label}</p>
                      <p className="mt-1 font-mono text-xs" style={{ color: isDarkMode ? "#d4d4d8" : "#667085" }}>{apiKey.keyPrefix}</p>
                      <p className="mt-1 text-xs" style={{ color: isDarkMode ? "#a1a1aa" : "#98a2b3" }}>
                        {copy.createdLastUsed(formatApiKeyTimestamp(apiKey.createdAt, language), formatApiKeyTimestamp(apiKey.lastUsedAt, language))}
                      </p>
                      {apiKey.expiresAt ? (
                        <p className="mt-1 text-xs font-medium" style={{ color: isApiKeyExpired(apiKey.expiresAt) ? (isDarkMode ? "#fda4af" : "#b42318") : isDarkMode ? "#d4d4d8" : "#475467" }}>
                          {isApiKeyExpired(apiKey.expiresAt) ? copy.expired : copy.expires} {formatApiKeyTimestamp(apiKey.expiresAt, language)}
                        </p>
                      ) : null}
                      {apiKey.revokedAt ? (
                        <p className="mt-1 text-xs font-medium" style={{ color: isDarkMode ? "#f5d0fe" : "#9f1239" }}>
                          {copy.revokedAt(formatApiKeyTimestamp(apiKey.revokedAt, language))}
                        </p>
                      ) : null}
                    </div>

                    <div className="flex shrink-0 flex-wrap items-center gap-2 sm:justify-end">
                      {apiKey.revokedAt ? (
                        <span
                          className="rounded-full bg-[#f2f4f7] px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-[#667085]"
                          style={isDarkMode ? { backgroundColor: "rgba(244, 114, 182, 0.12)", color: "#fbcfe8" } : undefined}
                        >
                          {copy.revokedBadge}
                        </span>
                      ) : null}
                      {apiKey.revokedAt ? (
                        <button
                          type="button"
                          onClick={() => void handleRevokeApiKey(apiKey.id, true)}
                          disabled={revokingApiKeyId === apiKey.id}
                          className="rounded-xl bg-white px-3 py-2 text-sm font-semibold text-[#b42318] ring-1 ring-[#fecaca] transition-colors hover:bg-[#fff5f5] disabled:cursor-not-allowed disabled:opacity-60"
                          style={isDarkMode ? { backgroundColor: "rgba(127, 29, 29, 0.2)", borderColor: "rgba(248, 113, 113, 0.28)", color: "#fecaca" } : undefined}
                          title={copy.deleteRevokedApiKey}
                        >
                          {revokingApiKeyId === apiKey.id ? copy.deleting : copy.delete}
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => void handleRevokeApiKey(apiKey.id, false)}
                          disabled={revokingApiKeyId === apiKey.id}
                          className="rounded-xl bg-white px-3 py-2 text-sm font-semibold text-[#b42318] ring-1 ring-[#fecaca] transition-colors hover:bg-[#fff5f5] disabled:cursor-not-allowed disabled:opacity-60"
                          style={isDarkMode ? { backgroundColor: "rgba(127, 29, 29, 0.2)", borderColor: "rgba(248, 113, 113, 0.28)", color: "#fecaca" } : undefined}
                        >
                          {revokingApiKeyId === apiKey.id ? copy.revoking : copy.revoke}
                        </button>
                      )}
                    </div>
                  </div>
                )) : (
                  <div className="rounded-[18px] border border-dashed border-[#d0d5dd] px-4 py-4 text-sm" style={isDarkMode ? { borderColor: "rgba(255, 255, 255, 0.18)", backgroundColor: "#242424", color: "#d4d4d8" } : { color: "#667085" }}>
                    {copy.noApiKeys}
                  </div>
                )}
              </div>

              {apiKeyError ? <p className="mt-4 text-sm font-semibold text-[#b42318]">{apiKeyError}</p> : null}
            </section>
          ) : null}

        </div>
        </div>
        </div>
      </AnimatedModal>
    </div>
  );
}

export function AppShell({
  activeView,
  availableViews,
  language,
  colorMode,
  branding,
  currentUser,
  currentUserEmail,
  currentUserAvatarUrl,
  currentUserRole,
  apiAccessEnabled,
  defaultApiKeyExpiryDays,
  navMenuMode,
  canManageApiKeys,
  canGenerateApiKeys,
  apiKeys,
  latestGeneratedApiKey,
  onNavigate,
  onColorModeChange,
  onLanguageChange,
  onNavMenuModeChange,
  onOpenProfileSettings,
  onGenerateApiKey,
  onDismissGeneratedApiKey,
  onRevokeApiKey,
  onAvatarUpload,
  onAvatarRemove,
  onUpdatePassword,
  onSignOut,
  search,
  searchFilter,
  searchCategory,
  searchCategoryOptions,
  searchResults,
  isSearchLoading,
  timezone,
  onOpenSearchResult,
  onSearchCategoryChange,
  onSearchFilterChange,
  onTimezoneChange,
  onSearchChange,
  children,
}: {
  activeView: View;
  availableViews: View[];
  language: AppLanguage;
  colorMode: ColorMode;
  branding: AppBranding;
  currentUser: string;
  currentUserEmail: string | null;
  currentUserAvatarUrl: string | null;
  currentUserRole: string | null;
  apiAccessEnabled: boolean;
  defaultApiKeyExpiryDays: number | null;
  navMenuMode: "labels" | "icons";
  canManageApiKeys: boolean;
  canGenerateApiKeys: boolean;
  apiKeys: ApiKeySummary[];
  latestGeneratedApiKey: string | null;
  onNavigate: (view: View) => void;
  onColorModeChange: (value: ColorMode) => void;
  onLanguageChange: (value: AppLanguage) => void;
  onNavMenuModeChange: (value: "labels" | "icons") => void;
  onOpenProfileSettings?: () => void;
  onGenerateApiKey: (label: string, expiryDays: number | null) => Promise<void>;
  onDismissGeneratedApiKey: () => void;
  onRevokeApiKey: (apiKeyId: string) => Promise<void>;
  onAvatarUpload?: (file: File) => Promise<void>;
  onAvatarRemove?: () => Promise<void>;
  onUpdatePassword?: (currentPassword: string, newPassword: string) => Promise<void>;
  onSignOut?: () => void;
  search: string;
  searchFilter: GlobalSearchFilter;
  searchCategory: string;
  searchCategoryOptions: string[];
  searchResults: GlobalSearchResult[];
  isSearchLoading: boolean;
  timezone: string;
  onOpenSearchResult: (result: GlobalSearchResult) => void;
  onSearchCategoryChange: (value: string) => void;
  onSearchFilterChange: (value: GlobalSearchFilter) => void;
  onTimezoneChange: (value: string) => void;
  onSearchChange: (value: string) => void;
  children: ReactNode;
}) {
  const isDarkMode = colorMode === "dark";
  const copy = appShellCopy[language];
  const brandRgb = hexToRgb(branding.primaryColor);
  const headerLogoSrc = branding.logoDataUrl ?? fallbackLogoUrl;
  const headerLogoAlt = branding.logoName ?? "Kuii logo";
  const isCompactNav = navMenuMode === "icons";
  const shellStyle = {
    "--brand-primary": branding.primaryColor,
    "--brand-primary-strong": mixColor(branding.primaryColor, -0.14),
    "--brand-primary-tint": mixColor(branding.primaryColor, 0.42),
    "--brand-primary-rgb": `${brandRgb.r}, ${brandRgb.g}, ${brandRgb.b}`,
    "--brand-primary-soft": `rgba(${brandRgb.r}, ${brandRgb.g}, ${brandRgb.b}, 0.1)`,
    "--brand-primary-soft-strong": `rgba(${brandRgb.r}, ${brandRgb.g}, ${brandRgb.b}, 0.16)`,
    "--brand-primary-shadow": `rgba(${brandRgb.r}, ${brandRgb.g}, ${brandRgb.b}, 0.2)`,
    "--brand-primary-shadow-strong": `rgba(${brandRgb.r}, ${brandRgb.g}, ${brandRgb.b}, 0.3)`,
    "--brand-primary-ring": `rgba(${brandRgb.r}, ${brandRgb.g}, ${brandRgb.b}, 0.18)`,
    backgroundColor: isDarkMode ? "#18181b" : "#f3f3f4",
    color: isDarkMode ? "#f8fafc" : "#1a1c1d",
  } as CSSProperties;
  const userInitials = getInitials(currentUser);
  const searchContainerRef = useRef<HTMLDivElement | null>(null);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isSearchVisible, setIsSearchVisible] = useState(false);
  const trimmedSearch = search.trim();
  const hasSearch = trimmedSearch.length > 0;
  const categoryFilteredSearchResults = searchCategory === "all"
    ? searchResults
    : searchResults.filter(result => result.category === searchCategory);
  const searchResultsByKind = categoryFilteredSearchResults.reduce(
    (counts, result) => {
      counts[result.kind] += 1;
      return counts;
    },
    { ticket: 0, customer: 0, knowledge: 0 } as Record<Exclude<GlobalSearchFilter, "all">, number>,
  );
  const filteredSearchResults = searchFilter === "all"
    ? categoryFilteredSearchResults
    : categoryFilteredSearchResults.filter(result => result.kind === searchFilter);

  useEffect(() => {
    setIsSearchOpen(hasSearch);
  }, [hasSearch]);

  useEffect(() => {
    if (isSearchOpen) {
      setIsSearchVisible(true);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setIsSearchVisible(false);
    }, 180);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isSearchOpen]);

  useEffect(() => {
    if (!isSearchOpen) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (searchContainerRef.current?.contains(event.target as Node)) {
        return;
      }

      setIsSearchOpen(false);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsSearchOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isSearchOpen]);

  return (
    <div className="min-h-screen antialiased selection:bg-[#88f8c5] selection:text-[#002114]" style={shellStyle}>
      <main className="min-h-screen">
        <header
          className="sticky top-0 z-40 backdrop-blur-xl"
          style={{
            backgroundColor: isDarkMode ? "rgba(24,24,27,0.84)" : "rgba(243,243,244,0.72)",
            boxShadow: isDarkMode ? "0 20px 40px rgba(0,0,0,0.24)" : "0 20px 40px rgba(26,28,29,0.04)",
          }}
        >
          <div className="flex w-full flex-col gap-3 px-4 py-3 sm:relative sm:h-16 sm:flex-row sm:items-center sm:justify-between sm:px-8 sm:py-0">
            <div className="flex items-center justify-between sm:contents">
              <div className="flex shrink-0 items-center gap-2.5">
                <img src={headerLogoSrc} alt={headerLogoAlt} className="h-8 w-8 shrink-0 object-contain" />
                <div>
                  <div className="font-brand text-base font-black tracking-[-0.04em]" style={{ color: isDarkMode ? "#f8fafc" : "#1a1c1d" }}>{branding.consoleName?.trim() || "Kuii"}</div>
                  <div className="font-headline text-[10px] font-semibold uppercase tracking-[0.24em]" style={{ color: isDarkMode ? "#a3a3a3" : "#94a3b8" }}>{copy.consoleLabel}</div>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <ProfileMenu
                  language={language}
                  currentUser={currentUser}
                  currentUserEmail={currentUserEmail}
                  currentUserAvatarUrl={currentUserAvatarUrl}
                  currentUserRole={currentUserRole}
                  apiAccessEnabled={apiAccessEnabled}
                  defaultApiKeyExpiryDays={defaultApiKeyExpiryDays}
                  navMenuMode={navMenuMode}
                  canManageApiKeys={canManageApiKeys}
                  canGenerateApiKeys={canGenerateApiKeys}
                  apiKeys={apiKeys}
                  latestGeneratedApiKey={latestGeneratedApiKey}
                  userInitials={userInitials}
                  themeMode={colorMode}
                  timezone={timezone}
                  onOpenSettings={onOpenProfileSettings}
                  onGenerateApiKey={onGenerateApiKey}
                  onDismissGeneratedApiKey={onDismissGeneratedApiKey}
                  onRevokeApiKey={onRevokeApiKey}
                  onAvatarUpload={onAvatarUpload}
                  onAvatarRemove={onAvatarRemove}
                  onUpdatePassword={onUpdatePassword}
                  onThemeModeChange={onColorModeChange}
                  onLanguageChange={onLanguageChange}
                  onNavMenuModeChange={onNavMenuModeChange}
                  onTimezoneChange={onTimezoneChange}
                  onSignOut={onSignOut}
                />
              </div>
            </div>

            <div className="w-full sm:pointer-events-none sm:absolute sm:left-1/2 sm:top-1/2 sm:w-[calc(100%-20rem)] sm:max-w-3xl sm:-translate-x-1/2 sm:-translate-y-1/2 sm:px-4 md:w-[calc(100%-22rem)] md:px-6 lg:w-[calc(100%-26rem)] xl:w-full xl:max-w-4xl">
              <div ref={searchContainerRef} className="relative mx-auto w-full max-w-2xl sm:pointer-events-auto">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                  <SearchIcon />
                </span>
                <input
                  type="text"
                  value={search}
                  onChange={event => {
                    onSearchChange(event.target.value);
                    setIsSearchOpen(event.target.value.trim().length > 0);
                  }}
                  onFocus={() => {
                    if (hasSearch) {
                      setIsSearchOpen(true);
                    }
                  }}
                  onKeyDown={event => {
                    if (event.key === "Escape") {
                      setIsSearchOpen(false);
                      return;
                    }

                    if (event.key === "Enter" && filteredSearchResults[0]) {
                      event.preventDefault();
                      setIsSearchOpen(false);
                      onOpenSearchResult(filteredSearchResults[0]);
                    }
                  }}
                  placeholder={copy.searchPlaceholder}
                  className="w-full rounded-full border-none py-2 pl-10 pr-4 text-sm outline-none transition-all duration-200 placeholder:text-slate-400"
                  style={{
                    boxShadow: "none",
                    backgroundColor: isDarkMode ? "rgba(45, 45, 45, 0.96)" : "#eeeeef",
                    color: isDarkMode ? "#f8fafc" : "#101828",
                  }}
                />

                {isSearchVisible ? (
                  <div className="absolute inset-x-0 top-full z-50 mt-3 flex justify-center px-1 sm:px-0">
                    <div
                      className={`search-results-panel w-full min-w-0 max-w-[min(58rem,calc(100vw-1rem))] overflow-hidden rounded-[28px] border shadow-[0_24px_60px_rgba(15,23,42,0.18)] backdrop-blur-xl sm:min-w-[42rem] ${isSearchOpen ? "search-results-panel--open" : "search-results-panel--closed"}`}
                      style={{
                        borderColor: isDarkMode ? "rgba(82, 82, 82, 0.95)" : "#eaecf0",
                        backgroundColor: isDarkMode ? "rgba(24, 24, 27, 0.98)" : "rgba(255, 255, 255, 0.98)",
                      }}
                    >
                    <div className="border-b px-4 py-3" style={{ borderColor: isDarkMode ? "rgba(82, 82, 82, 0.82)" : "#f2f4f7" }}>
                      <div className="flex flex-wrap items-center gap-2">
                        {([
                          { value: "all", count: categoryFilteredSearchResults.length },
                          { value: "ticket", count: searchResultsByKind.ticket },
                          { value: "customer", count: searchResultsByKind.customer },
                          { value: "knowledge", count: searchResultsByKind.knowledge },
                        ] as const).map(filterOption => {
                          const active = searchFilter === filterOption.value;

                          return (
                            <button
                              key={filterOption.value}
                              type="button"
                              onClick={() => onSearchFilterChange(filterOption.value)}
                              className="rounded-full px-3 py-1.5 text-xs font-semibold transition-colors"
                              style={{
                                backgroundColor: active ? "var(--brand-primary-soft)" : isDarkMode ? "rgba(45,45,45,0.96)" : "#f4f4f5",
                                color: active ? "var(--brand-primary)" : isDarkMode ? "#d4d4d8" : "#475467",
                              }}
                            >
                              {copy.searchFilters[filterOption.value]} · {filterOption.count}
                            </button>
                          );
                        })}

                        <label className="ml-auto min-w-[11rem]">
                          <span className="sr-only">{copy.searchFilterLabel}</span>
                          <select
                            value={searchCategory}
                            onChange={event => onSearchCategoryChange(event.target.value)}
                            className="w-full rounded-full border-none px-3 py-1.5 text-xs font-semibold outline-none"
                            style={{
                              backgroundColor: isDarkMode ? "rgba(45,45,45,0.96)" : "#f4f4f5",
                              color: isDarkMode ? "#d4d4d8" : "#475467",
                            }}
                          >
                            <option value="all">{copy.allCategories}</option>
                            {searchCategoryOptions.map(option => (
                              <option key={option} value={option}>
                                {option}
                              </option>
                            ))}
                          </select>
                        </label>
                      </div>
                    </div>

                    <div className="max-h-[22rem] overflow-y-auto p-2">
                      {isSearchLoading && filteredSearchResults.length === 0 ? (
                        <div className="flex items-center justify-center gap-3 rounded-[22px] px-4 py-8 text-center" style={{ backgroundColor: isDarkMode ? "rgba(39, 39, 42, 0.9)" : "#f8fafc" }}>
                          <span className="inline-flex items-center justify-center text-[var(--brand-primary)]" aria-label={copy.searchLoading}>
                            <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="h-6 w-6 animate-spin" xmlns="http://www.w3.org/2000/svg">
                              <circle cx="10" cy="10" r="7" stroke="currentColor" strokeOpacity="0.2" strokeWidth="2" />
                              <path d="M10 3a7 7 0 0 1 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                            </svg>
                          </span>
                          <p className="text-sm font-semibold" style={{ color: isDarkMode ? "#f8fafc" : "#101828" }}>{copy.searchLoading}</p>
                        </div>
                      ) : filteredSearchResults.length === 0 ? (
                        <div className="rounded-[22px] px-4 py-6 text-center" style={{ backgroundColor: isDarkMode ? "rgba(39, 39, 42, 0.9)" : "#f8fafc" }}>
                          <p className="text-sm font-semibold" style={{ color: isDarkMode ? "#f8fafc" : "#101828" }}>{copy.noResultsTitle}</p>
                          <p className="mt-1 text-xs" style={{ color: isDarkMode ? "#a1a1aa" : "#667085" }}>
                            {copy.noResultsDescription}
                          </p>
                        </div>
                      ) : (
                        filteredSearchResults.map(result => (
                          <button
                            key={result.id}
                            type="button"
                            onClick={() => {
                              setIsSearchOpen(false);
                              onOpenSearchResult(result);
                            }}
                            className="flex w-full items-start rounded-[22px] px-4 py-3 text-left transition-colors hover:bg-black/5"
                            style={{ color: isDarkMode ? "#f8fafc" : "#101828" }}
                          >
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-2">
                                <p className="truncate text-sm font-semibold">{result.title}</p>
                                <span
                                  className="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.18em]"
                                  style={{
                                    backgroundColor: isDarkMode ? "rgba(63,63,70,0.92)" : "#f4f4f5",
                                    color: isDarkMode ? "#d4d4d8" : "#667085",
                                  }}
                                >
                                  {copy.searchKinds[result.kind]}
                                </span>
                              </div>
                              <p className="mt-1 line-clamp-2 text-sm" style={{ color: isDarkMode ? "#d4d4d8" : "#475467" }}>{result.subtitle}</p>
                              <p className="mt-1 text-xs font-medium" style={{ color: isDarkMode ? "#a1a1aa" : "#667085" }}>{result.meta}</p>
                            </div>
                          </button>
                        ))
                      )}
                    </div>

                    {filteredSearchResults[0] ? (
                      <div className="border-t px-4 py-2 text-[11px] font-medium" style={{ borderColor: isDarkMode ? "rgba(82, 82, 82, 0.82)" : "#f2f4f7", color: isDarkMode ? "#a1a1aa" : "#667085" }}>
                        {copy.openTopResult}
                      </div>
                    ) : null}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </header>

        <div className="flex items-start">
          <aside
            className={`sticky top-24 flex h-[calc(100vh-6rem)] shrink-0 flex-col bg-transparent py-0 transition-[width] duration-150 ease-out will-change-[width] sm:top-16 sm:h-[calc(100vh-4rem)] ${isCompactNav ? "w-12" : "w-fit"}`}
          >
            <nav className={`${isCompactNav ? "flex-1 space-y-1" : "grid flex-1 auto-rows-min grid-cols-[max-content] gap-y-1"} custom-scrollbar min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain pt-1`}>
              {availableViews.map(item => {
                const active = item === activeView;
                const viewIcon = getViewIcon(item);
                const viewLabel = getViewLabel(item, language);

                return (
                  <button
                    key={item}
                    type="button"
                    onClick={() => onNavigate(item)}
                    aria-label={viewLabel}
                    title={viewLabel}
                    className={`group relative isolate flex items-center overflow-hidden font-headline text-[11px] uppercase tracking-[0.22em] transition-[color,transform] duration-150 ease-out hover:-translate-y-[1px] active:translate-y-0 ${isCompactNav ? "h-12 w-12 justify-center rounded-r-2xl px-0 text-center" : "min-w-max w-full gap-3 rounded-r-2xl py-3 pr-4 pl-2 text-left"} ${
                      active
                        ? "font-bold"
                        : `${isDarkMode ? "font-medium text-slate-400 hover:text-[var(--brand-primary)]" : "font-medium text-slate-500 hover:text-[var(--brand-primary)]"}`
                    }`}
                    style={active ? { color: branding.primaryColor } : { color: isDarkMode ? "#94a3b8" : undefined }}
                  >
                    <span
                      aria-hidden="true"
                      className={`absolute inset-y-0 right-0 rounded-r-2xl transition-[opacity,transform,background-color] duration-150 ease-out ${active ? "opacity-100 scale-100" : "opacity-0 scale-[0.96] group-hover:opacity-100 group-hover:scale-100"}`}
                      style={{
                        left: 0,
                        backgroundColor: isDarkMode
                          ? "rgba(255,255,255,0.05)"
                          : "#eeeeef",
                      }}
                    />
                    <span
                      aria-hidden="true"
                      className={`absolute right-0 top-1/2 h-7 -translate-y-1/2 rounded-full transition-[opacity,transform] duration-150 ease-out ${active ? "opacity-100 scale-y-100" : "opacity-0 scale-y-75"}`}
                      style={{ width: 3, backgroundColor: branding.primaryColor }}
                    />
                    <span className="relative z-10 inline-flex h-6 w-6 shrink-0 items-center justify-center transition-transform duration-150 ease-out group-hover:scale-[1.04]">{viewIcon}</span>
                    {isCompactNav ? <span className="sr-only">{viewLabel}</span> : <span className="relative z-10 whitespace-nowrap">{viewLabel}</span>}
                  </button>
                );
              })}
            </nav>
          </aside>

          <div className="min-h-[calc(100vh-4rem)] min-w-0 flex-1" style={{ backgroundColor: isDarkMode ? "#18181b" : "#f3f3f4" }}>{children}</div>
        </div>
      </main>
    </div>
  );
}
